import { queryOptions } from '@tanstack/react-query'

import { API_BASE, fetchJson, isApiError, path } from '@/api/client'
import { getJob } from '@/api/jobs'
import { subscribeJob } from '@/api/sse'
import type { Job, JobEvent } from '@/schema'
import type { Facing, PuppetPlan, SkelProjectBody, SkelProjectEnvelope, SkelProjectSummary, SkfAnimation, SkfRoot, ViewKind } from '@/skel/types'

/**
 * `/api/skel/*` — Skeleton Studio (docs/SKEL-EDITOR.md §4.10, §5.2).
 *
 * Rules this file is built around:
 *
 *  1. **Uploads are the raw request body** (D4): the server has no multipart parser for these
 *     routes, so a sheet, a `.skf`, a texture or a thumbnail goes up as the `Blob` itself with
 *     its own `Content-Type` (`file.type || 'application/octet-stream'`). The server sniffs the
 *     bytes and never trusts that header, but it still wants one.
 *  2. **Query keys are local** (`skelKeys`), because `api/keys.ts` is a one-owner file; every key
 *     starts with `'skel'` so `invalidateQueries({ queryKey: skelKeys.all() })` clears the lot.
 *  3. **Sheet shapes mirror `backend/app/skel/types.py` `to_json()`**: points are `[x, y]` arrays
 *     (sheet px, y down) or `null`, never `{x, y}` objects.
 *  4. The project document is edited in `features/skel/skelStore.ts`; `projectQuery` therefore
 *     has `staleTime: Infinity` — a background refetch would fight the editor's in-memory copy.
 */

/* -------------------------------------------------------------------------- */
/* Sheet shapes (backend/app/skel/types.py)                                    */
/* -------------------------------------------------------------------------- */

/** `[x, y]` in sheet pixels, x right, y down, origin top-left. */
export type SheetPoint = [number, number]

/** The PERSON (humanoid) part ids — the legacy vocabulary. Other body plans have their own (`features/skel/sheetPlans.ts`). */
export const PART_KINDS = ['head', 'torso', 'upper_arm', 'forearm', 'hand', 'upper_leg', 'lower_leg', 'foot', 'other', 'ignore'] as const
export type PartKind = (typeof PART_KINDS)[number]
/**
 * What a piece is: a part id of the sheet's body plan (`sheet_plans.part_ids(plan)`), e.g.
 * `upper_arm` for a person, `hind_leg` for a four-legged animal; `other` and `ignore` in every plan.
 */
export type PartId = PartKind | (string & {})

/** A body plan (`autorig.plan.BASE_PLANS`); the same ids as a puppet's `meta.plan`. */
export type PlanId = PuppetPlan
/** The "What is it?" answer: a plain choice, or "let Claude decide" (`sheet_plans.PLAN_CHOICES`). */
export type PlanChoice = 'auto' | 'humanoid' | 'quadruped' | 'bird' | 'fish' | 'serpent' | 'insect' | 'other'
/** What `POST …/analyze?plan=` accepts: a choice, or one of the plans hidden behind "Other thing". */
export type AnalyzePlan = PlanChoice | 'radial' | 'blob' | 'plant' | 'prop'
/** How the vision step ended (`off` = split without AI). */
export type AiStatus = 'off' | 'ok' | 'mock' | 'unavailable' | 'error' | 'timeout' | 'skipped' | 'unusable'
/** Who decided the body plan. */
export type PlanSource = 'ai' | 'user' | 'heuristic'

export const SHEET_VIEWS = ['front', 'side'] as const
export type SheetView = (typeof SHEET_VIEWS)[number]

/** Screen side (never anatomical): `l` = left of the torso centre on the sheet. */
export const SCREEN_SIDES = ['l', 'r', 'none'] as const
export type ScreenSide = (typeof SCREEN_SIDES)[number]

/** Side view only: `near` = the limb closer to the camera. */
export const LAYERS = ['near', 'far', 'none'] as const
export type Layer = (typeof LAYERS)[number]

export type LabelSource = 'ai' | 'heuristic' | 'user'
export type JointSource = 'geometry' | 'ai' | 'user'
export type SheetBackground = 'white' | 'alpha' | 'color'
export type SheetStatus = 'uploaded' | 'analyzing' | 'ready' | 'failed'

/** One connected foreground component of the sheet (`Blob.to_json`). `index` is 1-based and stable. */
export interface SheetBlob {
  index: number
  /** Tight box `[x0, y0, x1, y1]`, x1/y1 exclusive. */
  bbox: [number, number, number, number]
  /** The cut-out PNG's rect (bbox ± PAD, clamped). */
  crop: [number, number, number, number]
  area: number
  centroid: SheetPoint
  /** Tilt of the principal axis from straight down; positive = bottom end leans right. */
  angle_deg: number
  axis_len: number
  width: number
  /** Axis end with the smaller y. */
  e0: SheetPoint
  e1: SheetPoint
  /** The piece was cut out again by hand (a box edit, an added piece, a merge); absent = as split. */
  edited?: boolean
  /**
   * The cut-out is stored mirrored (`POST …/blobs/flip`): `x` = left/right, `y` = up/down. Absent =
   * as drawn. Every consumer (build, preview, outfit fit) reads the mirrored cut-out itself.
   */
  flip?: BlobFlip
}

/** Which way a piece's cut-out is mirrored (`segments.blobs[].flip`). */
export interface BlobFlip {
  x: boolean
  y: boolean
}

/** `x` = mirror left/right, `y` = mirror up/down. */
export type FlipAxis = 'x' | 'y'

/** `Blob` is the backend's name; exported under it without shadowing the DOM `Blob` in this module. */
export type { SheetBlob as Blob }

/** What a blob is and where it attaches (`PartLabel.to_json`). */
export interface PartLabel {
  index: number
  part: PartId
  view: SheetView
  side: ScreenSide
  layer: Layer
  /**
   * Chain parts only (the plan's part has `chain > 0`): the position counted from where the chain
   * attaches (tail piece 1 at the body) or the item number (leg pair, arm, fin, leaf). Absent / 0
   * for every other part — the server omits it then, so a person's labels are unchanged.
   */
  segment?: number
  /** Where the part hangs from its parent; `null` when not applicable. */
  proximal: SheetPoint | null
  /** Where its child hangs; `null` when not applicable. */
  distal: SheetPoint | null
  joints_source: JointSource
  confidence: number
  /**
   * A loose piece (`other`) pinned onto another piece of the same view: the spot on the host
   * (sheet px) where this piece's `proximal` (its pivot) sits. Absent / null = not pinned.
   */
  attach?: SheetPoint | null
}

/** The torso's attachment points (`TorsoJoints.to_json`). Side view: both shoulders = the near one. */
export interface TorsoJoints {
  neck: SheetPoint
  shoulder_l: SheetPoint
  shoulder_r: SheetPoint
  hip_l: SheetPoint
  hip_r: SheetPoint
  source: JointSource
}

/** Where an attachment point came from (`match` = found by matching a piece onto its drawn copy). */
export type AnchorSource = JointSource | 'match'

export interface ViewInfo {
  view: SheetView
  facing: Facing
  /** A person's neck / shoulders / hips; `null` for every other body plan (they use `anchors`). */
  torso: TorsoJoints | null
  /**
   * Other body plans: where each part attaches to its host piece (body / head / stem), sheet px,
   * keyed by the plan's anchor names (`shoulder`, `hip`, `neck`, `tail`, `ear_l`, `fin_2`, …).
   * Absent for a person.
   */
  anchors?: Record<string, SheetPoint>
  anchors_source?: Record<string, AnchorSource>
}

export interface Labels {
  source: LabelSource
  blobs: PartLabel[]
  views: Partial<Record<SheetView, ViewInfo>>
  /** Technical diagnostics for developers. Never shown to parents — `notices` are. */
  warnings: string[]
  model: string | null
  cost_usd: number
  /** The body plan the pieces are named for (absent on old sheets = `humanoid`). */
  plan?: PlanId
  plan_source?: PlanSource
  /** What the person answered to "What is it?" when the sheet was split. */
  plan_choice?: AnalyzePlan
  /** Claude's 2–5 plain words for what the picture shows ("a cartoon puppy"), or "". */
  subject?: string
  /** Claude's one plain sentence about the picture, or "". Never a question. */
  note?: string
  ai_status?: AiStatus
  /** Plain sentences for a parent ("The side view has no head."), at most 8. */
  notices?: string[]
}

export interface Segments {
  width: number
  height: number
  background: SheetBackground
  blobs: SheetBlob[]
  /**
   * Bumped by every piece edit (`POST …/pieces`); absent / 0 right after a split. The edit
   * request's `base_rev` must equal it.
   */
  rev?: number
  /** The next free piece number (a deleted number is never reused); absent / 0 after a split. */
  next_index?: number
}

/** `GET /api/skel/sheets/{id}` and every sheet mutation's answer (§4.10). */
export interface SheetEnvelope {
  id: string
  name: string
  w: number
  h: number
  status: SheetStatus
  job_id: number | null
  error: string | null
  created_at: string
  updated_at: string
  source_url: string
  canvas_url: string | null
  segments: Segments | null
  labels: Labels | null
  /** Blob index (as a string key, it is JSON) → URL of its cut-out PNG. */
  blob_urls: Record<string, string>
  /** The "What is it?" answer of the latest analysis (`null` before one / on old sheets). */
  plan_choice?: AnalyzePlan | null
  /** The person asked to stop waiting for Claude during the running analysis. */
  skip_ai?: boolean
}

/** A row of `GET /api/skel/sheets` — the sheet's meta without segments/labels. */
export interface SheetSummary {
  id: string
  name: string
  w: number
  h: number
  status: SheetStatus
  job_id: number | null
  error: string | null
  created_at: string
  updated_at: string
  source_url?: string
  blob_count?: number
}

/* -------------------------------------------------------------------------- */
/* Project / preset / job shapes                                               */
/* -------------------------------------------------------------------------- */

export type { SkelProjectEnvelope, SkelProjectSummary }

export interface PresetInfo {
  name: string
  label: string
  loop: boolean
  frames: number
}

/** `GET /api/skel/presets` — the server-side preset list per view kind (D9). */
export type PresetCatalog = Record<ViewKind, PresetInfo[]>

export interface BuildSheetRequest {
  name?: string
  views?: string[]
  /** Default `true`: generate the preset animations for each built view. */
  presets?: boolean
}

export interface CreateProjectRequest {
  name: string
  body?: SkelProjectBody
}

export interface PutProjectRequest {
  base_rev: number
  body: SkelProjectBody
}

export interface TextureUpload {
  sha: string
  w: number
  h: number
  url: string
}

export interface PresetsRequest {
  view: string
  names?: string[]
}

export interface AnimateRequest {
  view: string
  prompt: string
  fps?: number
  name?: string
}

export interface JobStarted {
  job_id: number
  /** Set when an analysis for this sheet was already running (the 409-safe answer). */
  already_running?: boolean
}

/** One Claude pass of a deep split: name the pieces, check the built skeleton, check the fixes. */
export type AiPassName = 'name' | 'check' | 'recheck'
export type AiPassStatus = 'ok' | 'timeout' | 'error' | 'unusable' | 'skipped' | 'mock' | 'not_run'

/** How one Claude pass went (`result.ai_passes`, also on the last progress events). */
export interface AiPass {
  pass: AiPassName | (string & {})
  status: AiPassStatus | (string & {})
  seconds: number
  /** Fixes the pass applied (check / recheck; 0 for the naming pass). */
  fixes: number
  cost_usd: number
}

/** `result` of a `skel.split` job (§4.9). */
export interface SplitJobResult {
  sheet_id: string
  blobs: number
  source: 'ai' | 'heuristic'
  /** Technical diagnostics (not for parents). */
  warnings: string[]
  plan?: PlanId
  plan_source?: PlanSource
  ai_status?: AiStatus
  /** Plain sentences for a parent. */
  notices?: string[]
  /** One entry per Claude pass (deep split only). */
  ai_passes?: AiPass[]
}

/* -------------------------------------------------------------------------- */
/* Piece edits (`POST …/pieces`) and the live preview (`POST …/preview`)        */
/* -------------------------------------------------------------------------- */

/** `[x0, y0, x1, y1]` in sheet px, x1/y1 exclusive. */
export type SheetBox = [number, number, number, number]

/** One piece edit; a batch applies them in order, all or nothing. */
export type SheetEditOp =
  | { op: 'box'; index: number; box: SheetBox }
  | { op: 'add'; box: SheetBox; view: SheetView | null }
  | { op: 'delete'; index: number }
  | { op: 'merge'; into: number; pieces: number[] }

/** `POST /api/skel/sheets/{id}/pieces`. */
export interface SheetEditRequest {
  /** Must equal `segments.rev` (0 right after a split); a stale one is a 409 with `current_rev`. */
  base_rev: number
  /** The client's current labels, applied first (as `PUT …/labels` would). */
  labels?: Labels | null
  /** 1..50 edits. */
  ops: SheetEditOp[]
}

export interface SheetEditResult {
  sheet: SheetEnvelope
  rev: number
  added: number[]
  changed: number[]
  removed: number[]
  /** Plain sentences for a parent. */
  notices: string[]
}

/** `POST /api/skel/sheets/{id}/preview`. */
export interface SheetPreviewRequest {
  /** Unsaved labels; null / absent = the stored ones. */
  labels?: Labels | null
  /** null = the plan's primary view that has pieces. */
  view?: SheetView | null
  /** 0..4 preset names; absent = `["idle"]`. */
  animations?: string[]
  /** Default true: fit each animation to this skeleton (plan kinds). */
  fit?: boolean
}

/** Where a preview texture's pixels come from: a piece's cut-out (maybe mirrored), or inline bytes. */
export interface PreviewTexture {
  blob?: number
  flip?: boolean
  /** `data:image/png;base64,…` — only when the picture matches neither the cut-out nor its mirror. */
  data?: string
}

export type FitStatus = 'ok' | 'fixed' | 'failed'

/** How an animation was fitted to the skeleton (`anim_fit.FitReport`). */
export interface FitReport {
  name: string
  status: FitStatus
  /** Technical: what was (or still is) wrong. Not for parents. */
  reasons: string[]
  /** Technical: what was changed. Not for parents. */
  changes: string[]
  /** A plain sentence, only when the build leaves the animation out. */
  notice: string | null
}

export interface SheetPreviewBone {
  bone: string
  index: number
  duplicate: boolean
}

export interface SheetPreviewResponse {
  sheet_id: string
  /** `segments.rev` the preview was built from. */
  rev: number
  plan: PlanId
  /** View kind: front|side for a person, the plan kind otherwise. */
  kind: string
  view: SheetView
  /** Views that have pieces, primary first. */
  views: SheetView[]
  facing: 'camera' | 'right'
  /** A valid SkelForm 0.8 root, or null (no body yet, an empty view) — `notices` say why. */
  root: SkfRoot | null
  /** One entry per `root.styles[0].textures` name. */
  textures: Record<string, PreviewTexture>
  /** Every textured bone and the piece it shows. */
  bones: SheetPreviewBone[]
  /** Presets this skeleton can play. */
  available: PresetInfo[]
  /** One per returned animation; empty for a person. */
  fit: FitReport[]
  /** Plain sentences for a parent. */
  notices: string[]
  /** Server time. */
  ms: number
}

/** `POST /api/skel/sheets/{id}/skip-ai` → 202 (409 once the job no longer waits for Claude). */
export interface SkipAiStarted {
  job_id: number
  skipping: true
}

/** `POST …/build` → the new project plus what the build noticed. */
export type BuildSheetResponse = SkelProjectEnvelope & {
  /** Technical diagnostics (not for parents). */
  warnings?: string[]
  /** Plain sentences for a parent ("The walk animation was left out because …"). */
  notices?: string[]
  /** Per built view, how each preset animation was fitted to the skeleton. */
  fit?: Record<string, FitReport[]>
}

/** One "What is it?" choice of `GET /api/skel/sheet-plans`. */
export interface SheetPlanChoiceJson {
  id: PlanChoice
  label: string
  hint: string
  plans: PlanId[]
}

/** `GET /api/skel/sheet-plans` — the server's part vocabulary per body plan (`sheet_plans.to_json()`). */
export interface SheetPlansJson {
  version: number
  choices: SheetPlanChoiceJson[]
  plans: Record<
    string,
    {
      id: PlanId
      label: string
      choice: PlanChoice
      views: SheetView[]
      kind: string
      facing: Partial<Record<SheetView, Facing>>
      body_part: string
      parts: Array<{
        id: string
        label: string
        noun: string
        family: string
        sided: boolean
        chain: number
        chain_label: string
        joints: string
        required: boolean
        proximal: string
        distal: string
      }>
      anchors: Partial<Record<SheetView, Array<{ id: string; label: string; host: string }>>>
    }
  >
  bone_labels: Record<string, string>
}

/** `result` of an `ai.skel_anim` job (§4.9): the client inserts `animation` itself. */
export interface AnimateJobResult {
  animation: SkfAnimation
  source: 'ai' | 'preset'
  notes: string
}

/* -------------------------------------------------------------------------- */
/* Fetchers — presets                                                          */
/* -------------------------------------------------------------------------- */

/** `GET /api/skel/presets`. */
export function getPresetCatalog(signal?: AbortSignal): Promise<PresetCatalog> {
  return fetchJson<PresetCatalog>('/api/skel/presets', { signal })
}

/* -------------------------------------------------------------------------- */
/* Fetchers — sheets                                                           */
/* -------------------------------------------------------------------------- */

/** The `Content-Type` a raw upload carries (§5.2): the file's own type, else octet-stream. */
export function rawContentType(file: Blob): string {
  return file.type || 'application/octet-stream'
}

function fileName(file: Blob): string | undefined {
  return typeof File !== 'undefined' && file instanceof File && file.name ? file.name : undefined
}

/** `POST /api/skel/sheets?name=` with the image as the raw body → `201 SheetEnvelope`. */
export function uploadSheet(file: Blob, opts: { name?: string; signal?: AbortSignal } = {}): Promise<SheetEnvelope> {
  return fetchJson<SheetEnvelope>('/api/skel/sheets', {
    method: 'POST',
    body: file,
    headers: { 'Content-Type': rawContentType(file) },
    query: { name: opts.name ?? fileName(file) },
    signal: opts.signal,
  })
}

/** `GET /api/skel/sheets`. */
export function listSheets(signal?: AbortSignal): Promise<{ items: SheetSummary[] }> {
  return fetchJson<{ items: SheetSummary[] }>('/api/skel/sheets', { signal })
}

/** `GET /api/skel/sheets/{id}`. */
export function getSheet(id: string, signal?: AbortSignal): Promise<SheetEnvelope> {
  return fetchJson<SheetEnvelope>(path`/api/skel/sheets/${id}`, { signal })
}

/**
 * `DELETE /api/skel/sheets/{id}` → 204: the picture, its pieces and labels go (a split still going is
 * stopped first). Puppets built from it keep working: they carry their own copy of every picture.
 */
export function deleteSheet(id: string): Promise<void> {
  return fetchJson<void>(path`/api/skel/sheets/${id}`, { method: 'DELETE' })
}

/** What `POST /api/skel/sheets/delete` did with each id. */
export interface SheetBulkDeleteResult {
  deleted: string[]
  /** Already gone. */
  missing: string[]
  /** Could not be removed (try again). */
  failed: string[]
}

/** `POST /api/skel/sheets/delete {ids}`: several sheets at once, each as `deleteSheet`; one never stops the others. */
export function deleteSheets(ids: readonly string[]): Promise<SheetBulkDeleteResult> {
  return fetchJson<SheetBulkDeleteResult>('/api/skel/sheets/delete', { method: 'POST', body: { ids } })
}

/**
 * `POST /api/skel/sheets/{id}/analyze?ai=&plan=[&keep_pieces=true]` → `202 {job_id}` (or
 * `{job_id, already_running: true}`). `plan` is the "What is it?" answer; left out, the server lets
 * Claude decide (`auto`). `keepPieces`: name the pieces again but keep the cut-outs, numbers and
 * hand edits (a 409 when the sheet was never split) — sent only when true.
 */
export function analyzeSheet(id: string, opts: { ai?: boolean; plan?: AnalyzePlan | null; keepPieces?: boolean } = {}): Promise<JobStarted> {
  return fetchJson<JobStarted>(path`/api/skel/sheets/${id}/analyze`, {
    method: 'POST',
    query: { ai: opts.ai ?? true, plan: opts.plan ?? undefined, keep_pieces: opts.keepPieces ? true : undefined },
  })
}

/**
 * `POST /api/skel/sheets/{id}/pieces` — one atomic batch of piece edits (resize a box, add a piece,
 * delete, merge). Rejects with `ApiError`: 409 `details.current_rev` (stale `base_rev`: reload) or
 * `details.status` (the sheet is being split / was never split); 422 `details.errors` (the message
 * is plain: "Nothing is drawn inside that box.").
 */
export function editSheetPieces(id: string, req: SheetEditRequest, signal?: AbortSignal): Promise<SheetEditResult> {
  return fetchJson<SheetEditResult>(path`/api/skel/sheets/${id}/pieces`, { method: 'POST', body: req, signal })
}

/**
 * `POST /api/skel/sheets/{id}/blobs/flip` `{indices, axis}` → the updated sheet envelope. Mirrors
 * each piece's stored cut-out (and its joints inside its crop box) left/right (`x`) or up/down
 * (`y`) in place and toggles its `flip`; flipping twice restores it exactly. The pieces' `blob_urls`
 * get a new `?v=`. The server reads the STORED labels: save unsent label edits first. Rejects with
 * `ApiError`: 404 (sheet / piece), 409 (the sheet is being split), 422 (bad axis / indices).
 */
export function flipBlobs(sheetId: string, indices: readonly number[], axis: FlipAxis, signal?: AbortSignal): Promise<SheetEnvelope> {
  return fetchJson<SheetEnvelope>(path`/api/skel/sheets/${sheetId}/blobs/flip`, { method: 'POST', body: { indices: [...indices], axis }, signal })
}

/**
 * `POST /api/skel/sheets/{id}/preview` — the skeleton one view of the sheet would build, with the
 * requested preset animations fitted to it. Nothing is stored. `root` is null (200) when nothing
 * can be built yet; `notices` say why.
 */
export function previewSheet(id: string, req: SheetPreviewRequest, signal?: AbortSignal): Promise<SheetPreviewResponse> {
  return fetchJson<SheetPreviewResponse>(path`/api/skel/sheets/${id}/preview`, { method: 'POST', body: req, signal })
}

/**
 * `POST /api/skel/sheets/{id}/skip-ai` — stop waiting for Claude: the running analysis names the
 * pieces from their shapes instead. Rejects with a 409 `ApiError` once it no longer waits.
 */
export function skipSheetAi(id: string): Promise<SkipAiStarted> {
  return fetchJson<SkipAiStarted>(path`/api/skel/sheets/${id}/skip-ai`, { method: 'POST' })
}

/** `GET /api/skel/sheet-plans` — the part vocabulary per body plan (mirrored in `features/skel/sheetPlans.ts`). */
export function getSheetPlans(signal?: AbortSignal): Promise<SheetPlansJson> {
  return fetchJson<SheetPlansJson>('/api/skel/sheet-plans', { signal })
}

/** `PUT /api/skel/sheets/{id}/labels` — the server marks them `source: "user"` and recomputes joints. */
export function putSheetLabels(id: string, labels: Labels): Promise<SheetEnvelope> {
  return fetchJson<SheetEnvelope>(path`/api/skel/sheets/${id}/labels`, { method: 'PUT', body: labels })
}

/** `POST /api/skel/sheets/{id}/build` → `201` the new project (+ `warnings` / plain `notices`). */
export function buildSheet(id: string, req: BuildSheetRequest = {}): Promise<BuildSheetResponse> {
  return fetchJson<BuildSheetResponse>(path`/api/skel/sheets/${id}/build`, { method: 'POST', body: req })
}

/* -------------------------------------------------------------------------- */
/* Fetchers — projects                                                         */
/* -------------------------------------------------------------------------- */

/** `POST /api/skel/import?name=` with a `.skf` as the raw body → `201` a one-view (`main`) project. */
export function importSkf(file: Blob, opts: { name?: string; signal?: AbortSignal } = {}): Promise<SkelProjectEnvelope> {
  const name = opts.name ?? fileName(file)?.replace(/\.skf$/i, '')
  return fetchJson<SkelProjectEnvelope>('/api/skel/import', {
    method: 'POST',
    body: file,
    headers: { 'Content-Type': rawContentType(file) },
    query: { name },
    signal: opts.signal,
  })
}

/** `GET /api/skel/projects` — newest first. */
export function listProjects(signal?: AbortSignal): Promise<{ items: SkelProjectSummary[] }> {
  return fetchJson<{ items: SkelProjectSummary[] }>('/api/skel/projects', { signal })
}

/** `POST /api/skel/projects` — without `body` the server makes a blank one-view project. */
export function createProject(req: CreateProjectRequest): Promise<SkelProjectEnvelope> {
  return fetchJson<SkelProjectEnvelope>('/api/skel/projects', { method: 'POST', body: req })
}

/** `GET /api/skel/projects/{id}`. */
export function getProject(id: string, signal?: AbortSignal): Promise<SkelProjectEnvelope> {
  return fetchJson<SkelProjectEnvelope>(path`/api/skel/projects/${id}`, { signal })
}

/**
 * `PUT /api/skel/projects/{id}` `{base_rev, body}` → the envelope with `rev` bumped. Rejects with
 * `ApiError`: `409` carries `details.current_rev`; `422` carries `details.errors` (a client bug).
 */
export function putProject(id: string, req: PutProjectRequest): Promise<SkelProjectEnvelope> {
  return fetchJson<SkelProjectEnvelope>(path`/api/skel/projects/${id}`, { method: 'PUT', body: req })
}

/** `DELETE /api/skel/projects/{id}` → 204. */
export function deleteProject(id: string): Promise<void> {
  return fetchJson<void>(path`/api/skel/projects/${id}`, { method: 'DELETE' })
}

/** `POST /api/skel/projects/{id}/tex` with an image as the raw body → the content-addressed sha. */
export function uploadTexture(projectId: string, file: Blob, signal?: AbortSignal): Promise<TextureUpload> {
  return fetchJson<TextureUpload>(path`/api/skel/projects/${projectId}/tex`, {
    method: 'POST',
    body: file,
    headers: { 'Content-Type': rawContentType(file) },
    signal,
  })
}

/** `PUT /api/skel/projects/{id}/thumb.png` with the PNG as the raw body → 204. */
export function putThumb(projectId: string, png: Blob): Promise<void> {
  return fetchJson<void>(path`/api/skel/projects/${projectId}/thumb.png`, {
    method: 'PUT',
    body: png,
    headers: { 'Content-Type': png.type || 'image/png' },
  })
}

/** `POST /api/skel/projects/{id}/presets` `{view, names?}` → SkelForm animations for this rig. */
export function generatePresets(projectId: string, req: PresetsRequest): Promise<{ animations: SkfAnimation[] }> {
  return fetchJson<{ animations: SkfAnimation[] }>(path`/api/skel/projects/${projectId}/presets`, { method: 'POST', body: req })
}

/** `POST /api/skel/projects/{id}/animate` `{view, prompt, fps?, name?}` → `202 {job_id}` (`ai.skel_anim`). */
export function startAnimate(projectId: string, req: AnimateRequest): Promise<JobStarted> {
  return fetchJson<JobStarted>(path`/api/skel/projects/${projectId}/animate`, { method: 'POST', body: req })
}

/* -------------------------------------------------------------------------- */
/* Outfits from a parts sheet (docs/SKEL-EDITOR.md "Outfits from a parts sheet")*/
/* -------------------------------------------------------------------------- */

/** Which sheet view dresses which project view (`null` targets = the server pairs them by kind). */
export interface OutfitTarget {
  view: string
  sheet_view: SheetView
}

/** The user's choice for one texture name: a sheet piece, or `null` = keep the old picture. */
export interface OutfitAssignment {
  tex: string
  part: number | null
}

/** A hand correction on top of the fit: world px (y up) and degrees about the fit anchor. */
export interface OutfitNudge {
  dx: number
  dy: number
  rot_deg: number
  scale: number
}

/** `POST /api/skel/projects/{id}/outfits/match` (extra keys are refused by the server). */
export interface OutfitMatchRequest {
  sheet_id: string
  /** Must equal the stored rev (409 `details.current_rev` otherwise). */
  base_rev: number
  targets?: OutfitTarget[] | null
  /** Per view, the styles that make up the "current look"; default the view's active styles. */
  base_styles?: Record<string, number[]> | null
  /** Per view; at most 400 each. */
  assignments?: Record<string, OutfitAssignment[]> | null
  /** Per view, per texture name; missing keys mean 0 / 0 / 0° / 1. */
  nudges?: Record<string, Record<string, Partial<OutfitNudge>>> | null
  /**
   * Unsaved labels (the `PUT …/labels` body shape), finalized exactly like a saved label (same
   * 2-decimal rounding); default: the sheet's saved labels.
   */
  labels?: Labels | null
  /**
   * `false` = a preview: nothing goes into the texture pool and each row's `new` comes back inline
   * (`OutfitInlineTex`, see `previewOutfitMatch`). Default true.
   */
  store?: boolean
}

/** How badly the fit bends the sheet's pieces to this skeleton (plain `reasons` for the user). */
export type OutfitDistortionLevel = 'ok' | 'warn' | 'bad'

export interface OutfitDistortion {
  /** Body stretch ratio (≥ 1; 1 = kept its proportions). */
  body_stretch: number
  /** Smallest / largest limb scale along the bone (1 = kept its length). */
  limb_scale_min: number
  limb_scale_max: number
  /** Pieces of the sheet view that dress nothing. */
  unused_parts: number
  level: OutfitDistortionLevel
  reasons: string[]
}

/** How a row found its piece. */
export type OutfitMatchKind = 'name' | 'sibling' | 'shape' | 'user' | 'none'
/** How the piece was fitted onto the bone. */
export type OutfitFitKind = 'joints' | 'torso' | 'anchor' | 'centre'

/** A picture already in the project. */
export interface OutfitTexRef {
  style: number
  sha: string
  w: number
  h: number
}

/** A fitted picture the server baked into the project's texture pool (not referenced until applied). */
export interface OutfitNewTex {
  sha: string
  w: number
  h: number
  url: string
}

/** Informational: what the fit did to the piece (4 decimals). */
export interface OutfitTransform {
  scale_along: number
  scale_across: number
  rot_deg: number
  dx: number
  dy: number
}

/** One texture NAME of the current look: the unit an outfit swaps. */
export interface OutfitRow {
  tex: string
  /** Bones whose rest picture is this name, reference bone first. */
  bones: string[]
  bone_ids: number[]
  /** Canonical slot the reference bone resolved to (`upper_arm_l`, `head`, …), or null. */
  canonical: string | null
  old: OutfitTexRef
  /** Sheet piece used; null = keeps the old picture. */
  part: number | null
  match: OutfitMatchKind
  /** name .95, sibling .5, shape ≤ .8, user 1, none 0. */
  confidence: number
  fit: OutfitFitKind | null
  new: OutfitNewTex | null
  transform: OutfitTransform | null
  /** Some of the art fell outside the picture's frame. */
  clipped: boolean
  warnings: string[]
}

/** A non-ignored piece of the sheet view that was matched. */
export interface OutfitPart {
  part: number
  label: { part: PartId; view: SheetView; side: ScreenSide; layer: Layer; segment?: number }
  /** The bone slot the piece assembled into (`upper_arm_l`, `part_7`), or null. */
  slot: string | null
  blob_url: string
  /** Texture names this piece dresses. */
  used_by: string[]
}

export interface OutfitViewMatch {
  view: string
  sheet_view: SheetView
  kind: string
  base_styles: number[]
  /** Old world px per new world px. */
  scale: number
  rows: OutfitRow[]
  parts: OutfitPart[]
  unmatched_parts: number[]
  unmatched_textures: string[]
  warnings: string[]
  /** How much the fit distorts the pieces (absent on older servers). */
  distortion?: OutfitDistortion
}

/** The plan: nothing in the project changes until the editor applies it (one undoable edit). */
export interface OutfitMatch {
  project_id: string
  project_rev: number
  sheet_id: string
  sheet_name: string
  suggested_name: string
  /** Fitted views only. */
  views: Record<string, OutfitViewMatch>
  skipped: Array<{ view: string; reason: string }>
  warnings: string[]
  /**
   * True when a view's distortion is `bad` or the sheet has bones this character lacks (ears, a
   * tail, separate far legs): an outfit cannot give it the sheet's shape — rebuild instead.
   */
  skeleton_mismatch?: boolean
  /** `POST …/rebuild-from-sheet` can build this character's skeleton from the sheet. */
  rebuild_available?: boolean
  /** False for a preview match (`store: false`): nothing entered the texture pool. */
  stored?: boolean
}

/** A fitted picture returned inline by a `store: false` match (nothing was written to the pool). */
export interface OutfitInlineTex {
  data_url: string
  w: number
  h: number
}

export type OutfitInlineRow = Omit<OutfitRow, 'new'> & { new: OutfitInlineTex | null }
export type OutfitInlineViewMatch = Omit<OutfitViewMatch, 'rows'> & { rows: OutfitInlineRow[] }
/** The answer of a preview match (`store: false`). */
export type OutfitInlineMatch = Omit<OutfitMatch, 'views'> & { views: Record<string, OutfitInlineViewMatch> }

/**
 * The same fit as `matchOutfit`, as a PREVIEW (`store: false`): the pictures come back as data
 * URLs and nothing is added to the project's texture pool. With `labels`, the sheet's unsaved
 * labels are used (finalized like saved ones), so the review can show the pieces on the character
 * before anything is saved. Same errors as `matchOutfit`.
 */
export function previewOutfitMatch(projectId: string, req: Omit<OutfitMatchRequest, 'store'>, signal?: AbortSignal): Promise<OutfitInlineMatch> {
  return fetchJson<OutfitInlineMatch>(path`/api/skel/projects/${projectId}/outfits/match`, { method: 'POST', body: { ...req, store: false }, signal })
}

/* ------------------------------------------------------------ rebuild the skeleton from a sheet */

export interface RebuildFromSheetRequest {
  sheet_id: string
  /** Must equal the stored rev (409 `details.current_rev` otherwise). */
  base_rev: number
  /** Unsaved labels, as for `OutfitMatchRequest.labels`; default the saved ones. */
  labels?: Labels | null
  /** Views to build (default: every view the sheet supports). */
  views?: string[] | null
}

export type RebuildAnimStatus = 'ok' | 'adjusted' | 'dropped' | 'new'

export interface RebuildReport {
  views: Record<string, { bones_before: number; bones_after: number }>
  /** Every animation of the new rig, and every old one that could not come along. */
  animations: Array<{ name: string; status: RebuildAnimStatus; reason?: string; /** The view the clip belongs to. */ view?: string }>
  kept_outfits: boolean
  notes: string[]
}

export interface RebuildFromSheetResponse {
  project_rev: number
  /**
   * The whole new body: the sheet's skeleton for every view it supports, its pictures already in
   * the project's texture pool, the character's name / story settings kept. NOT saved: the editor
   * applies it as one undoable edit and the person's Save stores it (like an outfit).
   */
  body: SkelProjectBody
  report: RebuildReport
}

/**
 * `POST /api/skel/projects/{id}/rebuild-from-sheet` → a new body built from the sheet's own
 * skeleton (never written by the server). Rejects with `ApiError`: 409 `details.current_rev`
 * (stale `base_rev`), 409 (sheet not analysed), 422 (the sheet has no usable pieces).
 */
export function rebuildFromSheet(projectId: string, req: RebuildFromSheetRequest, signal?: AbortSignal): Promise<RebuildFromSheetResponse> {
  return fetchJson<RebuildFromSheetResponse>(path`/api/skel/projects/${projectId}/rebuild-from-sheet`, { method: 'POST', body: req, signal })
}

/**
 * `POST /api/skel/projects/{id}/outfits/match` → the fitted plan. Rejects with `ApiError`: 409
 * `details.current_rev` (stale `base_rev`: save, then retry) or `details.status` (sheet not
 * analysed); 404 (project / sheet / view); 422 `details.errors` (bad assignment, nothing fits).
 */
export function matchOutfit(projectId: string, req: OutfitMatchRequest, signal?: AbortSignal): Promise<OutfitMatch> {
  return fetchJson<OutfitMatch>(path`/api/skel/projects/${projectId}/outfits/match`, { method: 'POST', body: req, signal })
}

/* -------------------------------------------------------------------------- */
/* URL helpers (bytes the browser loads directly)                              */
/* -------------------------------------------------------------------------- */

const apiUrl = (p: string): string => `${API_BASE}${p}`

/** A project texture by content hash; served `immutable`, so the URL is the cache key. */
export function texUrl(projectId: string, sha: string): string {
  return apiUrl(path`/api/skel/projects/${projectId}/tex/${sha}.png`)
}

export function sheetSourceUrl(sheetId: string): string {
  return apiUrl(path`/api/skel/sheets/${sheetId}/source.png`)
}

/** The numbered overlay the AI saw (only after an analysis). */
export function sheetCanvasUrl(sheetId: string): string {
  return apiUrl(path`/api/skel/sheets/${sheetId}/canvas.png`)
}

/** The transparent cut-out of blob `index` (1-based). */
export function blobUrl(sheetId: string, index: number): string {
  return apiUrl(path`/api/skel/sheets/${sheetId}/blobs/${index}.png`)
}

/**
 * The cut-out of piece `index` as the envelope lists it: `blob_urls` carry a `?v=` version that
 * changes only when that piece is cut out again, so a re-cut piece reloads and the rest stay
 * cached. Falls back to the plain URL (older servers, a piece the envelope does not list yet).
 */
export function blobSrc(sheet: Pick<SheetEnvelope, 'id' | 'blob_urls'>, index: number): string {
  const listed = sheet.blob_urls?.[String(index)]
  if (listed) return /^(https?:|data:|blob:)/i.test(listed) ? listed : apiUrl(listed)
  return blobUrl(sheet.id, index)
}

/** `.skf` download of one view (`Content-Disposition: attachment`). */
export function exportSkfUrl(projectId: string, view: string): string {
  return apiUrl(path`/api/skel/projects/${projectId}/export/${encodeURIComponent(view)}.skf`)
}

/** The project card thumbnail; `rev` busts the browser cache after a save. */
export function thumbUrl(projectId: string, rev?: number | null): string {
  const base = apiUrl(path`/api/skel/projects/${projectId}/thumb.png`)
  return rev == null ? base : `${base}?v=${rev}`
}

/* -------------------------------------------------------------------------- */
/* Query keys + options                                                        */
/* -------------------------------------------------------------------------- */

export const skelKeys = {
  all: () => ['skel'] as const,
  projects: () => ['skel', 'projects'] as const,
  project: (id: string) => ['skel', 'project', id] as const,
  sheets: () => ['skel', 'sheets'] as const,
  sheet: (id: string) => ['skel', 'sheet', id] as const,
  /** One live preview: the sheet's pieces (`rev`), the labels (a hash), the view and the animations. */
  sheetPreview: (id: string, rev: number, labelsHash: string, view: string | null, anims: readonly string[]) =>
    ['skel', 'sheet-preview', id, rev, labelsHash, view, [...anims]] as const,
  presets: () => ['skel', 'presets'] as const,
  sheetPlans: () => ['skel', 'sheet-plans'] as const,
}

const noRetryOn404 = (count: number, err: unknown): boolean => count < 2 && !(isApiError(err) && err.status === 404)

/** While a sheet is being analysed its envelope is re-read this often (the SSE stream is faster). */
export const SHEET_POLL_MS = 1_000

/**
 * The document the editor loads. `staleTime: Infinity` on purpose: once the store owns the
 * document a background refetch would silently fight the operator's edits (as `annotationQuery`).
 */
export const projectQuery = (id: string) =>
  queryOptions({
    queryKey: skelKeys.project(id),
    queryFn: ({ signal }) => getProject(id, signal),
    staleTime: Infinity,
    enabled: id.length > 0,
    retry: noRetryOn404,
  })

export const projectsQuery = () =>
  queryOptions({
    queryKey: skelKeys.projects(),
    queryFn: ({ signal }) => listProjects(signal),
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  })

export const sheetsQuery = () =>
  queryOptions({
    queryKey: skelKeys.sheets(),
    queryFn: ({ signal }) => listSheets(signal),
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  })

/** Polls every second while `status === 'analyzing'`, then stops by itself. */
export const sheetQuery = (id: string) =>
  queryOptions({
    queryKey: skelKeys.sheet(id),
    queryFn: ({ signal }) => getSheet(id, signal),
    staleTime: 5_000,
    enabled: id.length > 0,
    retry: noRetryOn404,
    refetchInterval: (query) => (query.state.data?.status === 'analyzing' ? SHEET_POLL_MS : false),
  })

/** The preset list only changes with a deploy. */
export const presetsQuery = () =>
  queryOptions({
    queryKey: skelKeys.presets(),
    queryFn: ({ signal }) => getPresetCatalog(signal),
    staleTime: 5 * 60_000,
  })

/** The part vocabulary only changes with a deploy. */
export const sheetPlansQuery = () =>
  queryOptions({
    queryKey: skelKeys.sheetPlans(),
    queryFn: ({ signal }) => getSheetPlans(signal),
    staleTime: Infinity,
  })

/* -------------------------------------------------------------------------- */
/* Jobs                                                                        */
/* -------------------------------------------------------------------------- */

export type JobPhaseUpdate = {
  status: Job['status']
  phase: string | null
  /** 0..1, or null when the job cannot honestly say (e.g. while waiting for Claude). */
  pct: number | null
  /** Parent-facing copy, e.g. "Finding the pieces…". */
  msg: string | null
  /**
   * The phase's structured extras (`skel.split`: `step`, `pieces`, `elapsed_s`, `limit_s`, …):
   * polling reads `job.progress.data`, the SSE stream the event's own keys minus phase/pct/msg.
   * `null` when the job sent none.
   */
  data: Record<string, unknown> | null
}

/** The SSE event's extras: everything but phase / pct / msg (null when nothing is left). */
function eventData(data: JobEvent['data'] | null | undefined): Record<string, unknown> | null {
  if (!data) return null
  const { phase: _phase, pct: _pct, msg: _msg, ...rest } = data
  return Object.keys(rest).length ? rest : null
}

/** `job.progress.data`, or null when absent / empty. */
function progressData(job: Job): Record<string, unknown> | null {
  const d = job.progress?.data
  return d && typeof d === 'object' && Object.keys(d).length ? (d as Record<string, unknown>) : null
}

/** A job that ended `failed` or `cancelled`; `message` is the server's error sentence when there is one. */
export class JobFailedError extends Error {
  readonly jobId: number
  readonly status: 'failed' | 'cancelled'
  readonly job: Job | null

  constructor(jobId: number, status: 'failed' | 'cancelled', message: string, job: Job | null = null) {
    super(message)
    this.name = 'JobFailedError'
    this.jobId = jobId
    this.status = status
    this.job = job
  }
}

export interface WaitForJobOptions {
  /** Poll period, ms (default 1000). */
  intervalMs?: number
  /**
   * Also open the per-job SSE stream (`subscribeJob`) so phases arrive as they happen and a
   * terminal event cuts the current poll wait short. Polling stays the source of truth.
   */
  stream?: boolean
  /** Consecutive network / 5xx poll failures tolerated before giving up (default 5). */
  maxPollErrors?: number
}

function abortError(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('Aborted', 'AbortError')
}

/**
 * Resolve with a job's `result` once it succeeds; reject with `JobFailedError` when it fails or
 * is cancelled, with the abort reason when `signal` aborts, or with the `ApiError` of a poll that
 * cannot succeed (404, or too many network errors in a row). `onProgress` is called whenever
 * status / phase / pct / msg change.
 */
export async function waitForJob<R = Record<string, unknown>>(
  jobId: number,
  onProgress?: (update: JobPhaseUpdate) => void,
  signal?: AbortSignal,
  opts: WaitForJobOptions = {},
): Promise<R> {
  const intervalMs = opts.intervalMs ?? 1_000
  const maxPollErrors = opts.maxPollErrors ?? 5
  let lastKey = ''
  const emit = (u: JobPhaseUpdate): void => {
    const key = JSON.stringify([u.status, u.phase, u.pct, u.msg, u.data])
    if (key === lastKey) return
    lastKey = key
    onProgress?.(u)
  }

  let wake: (() => void) | null = null
  let unsubscribe: (() => void) | null = null
  if (opts.stream) {
    unsubscribe = subscribeJob(jobId, (ev: JobEvent) => {
      if (ev.kind === 'progress' || ev.kind === 'started') {
        emit({ status: 'running', phase: ev.data.phase ?? null, pct: ev.data.pct ?? null, msg: ev.data.msg ?? null, data: eventData(ev.data) })
      } else if (ev.kind === 'succeeded' || ev.kind === 'failed' || ev.kind === 'cancelled') {
        wake?.()
      }
    })
  }

  const sleep = (ms: number): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      const finish = (): void => {
        clearTimeout(timer)
        signal?.removeEventListener('abort', onAbort)
        wake = null
        resolve()
      }
      const onAbort = (): void => {
        clearTimeout(timer)
        wake = null
        reject(abortError(signal!))
      }
      const timer = setTimeout(finish, ms)
      wake = finish
      signal?.addEventListener('abort', onAbort, { once: true })
    })

  try {
    let pollErrors = 0
    for (;;) {
      if (signal?.aborted) throw abortError(signal)
      let job: Job
      try {
        job = await getJob(jobId, signal)
        pollErrors = 0
      } catch (err) {
        if (signal?.aborted) throw abortError(signal)
        const transient = isApiError(err) && (err.status === 0 || err.status >= 500)
        if (!transient || ++pollErrors > maxPollErrors) throw err
        await sleep(intervalMs)
        continue
      }
      emit({ status: job.status, phase: job.progress?.phase ?? null, pct: job.progress?.pct ?? null, msg: job.progress?.msg ?? null, data: progressData(job) })
      if (job.status === 'succeeded') return (job.result ?? {}) as R
      if (job.status === 'failed') throw new JobFailedError(jobId, 'failed', job.error || 'The job failed.', job)
      if (job.status === 'cancelled') throw new JobFailedError(jobId, 'cancelled', 'The job was cancelled.', job)
      await sleep(intervalMs)
    }
  } finally {
    unsubscribe?.()
  }
}
